import {useSnackbar} from 'notistack';
import React, {useState} from 'react';
import {Box, Button, DialogActions, Grid, Typography} from '@mui/material';
import {useSubmitHandover} from '../../hooks/useSubmitHandover';
import {useReviewContext} from '../../contexts/ReviewContext';
import {usePrePrecursorGQL} from '../../components/PrecursorForm/usePrePrecursorGQL';

import {BusinessUnitEnum, GlobalPermissionEnum} from '../../schema-types/globalTypes';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';
import AutoField, {ActionType} from '../../components/AutoForm/AutoFieldNew';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {HandoverValidationMessage} from '../../components/FooterNavigation/HandoverValidationMessage';
import {useReviewDataValidation} from '../../hooks/useClientDataValidation';
import {KeyClientDataBanner} from '../../components/KeyClientDataBanner/KeyClientDataBanner';
import {useAccountContext} from '../../contexts/AccountContext';

type screens = 'actions-for-junior-handover' | 'approve' | 'reject';

export const HandoverSeniorAdviserSignOffModal: React.FC<{onClose: () => void}> = ({
  onClose,
}) => {
  const {reviewValidation: review} = useReviewContext();
  const [screen, setScreen] = useState<screens>('actions-for-junior-handover');

  const {fullSubmit, softSubmit} = useSubmitHandover();

  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {updateReview} = usePrePrecursorGQL();

  const handleSeniorReviewApproval = async (values: formType) => {
    closeSnackbar();
    if (!review) {
      enqueueSnackbar('Review data is not available', {variant: 'error'});
      return;
    }

    const res = await updateReview({
      variables: {
        reviewId: review.id,
        signoffApprovalReason: !values.approvalReason
          ? 'Approved'
          : values.approvalReason,
      },
    });

    if (res.data?.updateReview?.ok) {
      enqueueSnackbar('The current review is open', {variant: 'success'});
    } else {
      enqueueSnackbar('An error occurred when re-opening the current review', {
        variant: 'error',
      });
    }

    await fullSubmit();
    onClose();
  };

  const handleSeniorReviewRejection = async (values: formType) => {
    closeSnackbar();
    if (!review) {
      enqueueSnackbar('Review data is not available', {variant: 'error'});
      return;
    }

    const res = await updateReview({
      variables: {
        reviewId: review.id,
        signoffRejectedReason: values.rejectedReason,
      },
    });

    if (res.data?.updateReview?.ok) {
      enqueueSnackbar('The current review is open', {variant: 'success'});
    } else {
      enqueueSnackbar('An error occurred when re-opening the current review', {
        variant: 'error',
      });
    }

    await softSubmit();
    onClose();
  };

  return (
    <>
      <FeatureBoundary
        showErrorIfDenied={false}
        features={[GlobalPermissionEnum.can_access_pad_senior_handover_modal_FE]}
      >
        {screen === 'actions-for-junior-handover' && (
          <JuniorHandoverActions setScreen={setScreen} />
        )}
        {screen === 'approve' && (
          <ApprovalFormWrapped
            formFields={[]}
            submit={(values) => {
              handleSeniorReviewApproval(values);
            }}
            onClose={() => setScreen('actions-for-junior-handover')}
          />
        )}
        {screen === 'reject' && (
          <RejectionFormWrapped
            formFields={[]}
            submit={(values) => {
              handleSeniorReviewRejection(values);
            }}
            onClose={() => setScreen('actions-for-junior-handover')}
          />
        )}
      </FeatureBoundary>
    </>
  );
};

type modalProps = {
  onClose: () => void;
};

type formType = {
  rejectedReason: string;
  approvalReason: string;
};

const JuniorHandoverActions: React.FC<{
  setScreen: React.Dispatch<React.SetStateAction<screens>>;
}> = ({setScreen}) => {
  return (
    <>
      <Typography pb={3} variant="h5">
        Senior adviser PAD review Options
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setScreen('approve')}
          >
            Approved Review
          </Button>
          <Box mt={1}>
            <Typography>
              I agree the handover notes are adequate and approve of the advice points
              recommended in this review.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setScreen('reject');
            }}
          >
            Send For Editing
          </Button>
          <Box mt={1}>
            <Typography>
              I do not think the handover notes are adequate or think the requirements
              need editing prior to submission. This review needs to be sent back to the
              junior adviser.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

const RejectionForm: React.FC<modalProps> = ({onClose}) => {
  const {submitForm} = useAutoForm();
  return (
    <>
      <Typography pb={3} variant="h5">
        Send For Editing
      </Typography>
      <Typography mb={2} variant="h6">
        Please provide detail as to why you have not approved the EHONs and requirements,
        and detail what the junior adviser needs to do/consider before resending this for
        sign off.
      </Typography>
      <AutoField
        multiline
        minRows={3}
        field={{
          id: 'rejectedReason',
          type: ActionType.text,
          label: '',
          placeholder: 'Provide feedback here...',
          initialValue: '',
        }}
        data-testid="edit-details-reject"
      />
      <Typography mb={2} mt={2} variant="h6">
        On submission, a task will be sent to the junior adviser to edit the review. Do
        you want to confirm?
      </Typography>

      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined">
          Cancel
        </Button>
        <Button onClick={() => submitForm()} color="primary" variant="contained">
          Confirm
        </Button>
      </DialogActions>
    </>
  );
};

export const RejectionFormWrapped = withAutoFormNew<formType, modalProps>(
  RejectionForm,
  undefined,
  {
    debugPosition: 'bottom',
  }
);

const ApprovalForm: React.FC<modalProps> = ({onClose}) => {
  const {submitForm} = useAutoForm();
  const {accountType} = useAccountContext();
  const {allValid} = useReviewContext();
  const {reviewDataIssues, hasReviewDataIssues} = useReviewDataValidation();
  const canFullSubmit = allValid && !hasReviewDataIssues;
  const isSFP = accountType === BusinessUnitEnum.SFP;
  return (
    <>
      <Typography pb={3} variant="h5">
        Approve Review
      </Typography>
      {hasReviewDataIssues && (
        <KeyClientDataBanner issues={reviewDataIssues} isSFP={isSFP} />
      )}
      <Typography mb={2} variant="h6">
        On submission of this review, you are confirming you agree you are happy with the
        review notes and requirements from the junior adviser.
      </Typography>
      <Typography mb={2} variant="h6">
        If you would like to add any notes for the junior adviser, please leave your
        comments below
      </Typography>
      <AutoField
        multiline
        minRows={3}
        field={{
          id: 'approvalReason',
          type: ActionType.text,
          label: '',
          placeholder: 'Provide feedback here...',
          initialValue: '',
        }}
        data-testid="edit-details-approve"
      />
      <Typography mb={2} variant="h6">
        On submission, no further changes to the review notes will be possible.
      </Typography>
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={() => submitForm()}
          color="primary"
          variant="contained"
          disabled={!canFullSubmit}
        >
          Confirm
        </Button>
      </DialogActions>
      {!allValid && <HandoverValidationMessage />}
    </>
  );
};

export const ApprovalFormWrapped = withAutoFormNew<formType, modalProps>(
  ApprovalForm,
  undefined,
  {
    debugPosition: 'bottom',
  }
);
